import { memo, useMemo } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { isVetApp } from "../../messaging/helpers/isVetApp";
import {
  formatCommunicationsDateTimeDisplay,
  formatLastAppointmentDisplay,
  formatPatientBirthDateDisplay,
  formatPatientPhoneDisplay,
  formatPatientTaxIdentifierDisplay,
} from "../helpers/formatCrmCommunicationsDisplay";
import { isLogicalCpfPatientId } from "../helpers/crmVetTutorRecipient";
import { formatPatientListLabel } from "../patientLabels";
import type { CrmPatientSummary, FluxZapCommunicationRecord } from "../types";
import PatientNameCell from "./PatientNameCell";
import { communicationsRankTableBodyCellSx } from "./communicationsSubsectionStyles";

/** Bloco reutilizado nos dialogs de detalhe (tutor/pet ou paciente humano). */
function CommunicationsRecipientDetailSection({
  patient,
  vetApp,
  petNamesByTutorLogicalId,
  fallbackReference,
}: {
  patient: CrmPatientSummary | undefined;
  vetApp: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  fallbackReference?: string;
}) {
  const isTutorRecipient = Boolean(
    patient && vetApp && isLogicalCpfPatientId(patient.patientId),
  );

  const petNames = useMemo(() => {
    if (!isTutorRecipient || !patient) return [];
    return petNamesByTutorLogicalId?.get(patient.patientId) ?? [];
  }, [isTutorRecipient, patient, petNamesByTutorLogicalId]);

  const sectionTitle = isTutorRecipient
    ? "Tutor"
    : vetApp
      ? "Pet"
      : "Paciente";

  if (!patient) {
    return (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          {sectionTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Referência: {fallbackReference ?? "—"}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" fontWeight={700}>
        {sectionTitle}
      </Typography>
      <PatientNameCell patient={patient} />
      {patient.taxIdentifierValue ? (
        <Typography variant="body2">
          CPF:{" "}
          {formatPatientTaxIdentifierDisplay(patient.taxIdentifierValue)}
        </Typography>
      ) : null}
      {patient.phone ? (
        <Typography variant="body2">
          Telefone: {formatPatientPhoneDisplay(patient.phone)}
        </Typography>
      ) : null}
      {patient.email ? (
        <Typography variant="body2">E-mail: {patient.email}</Typography>
      ) : null}
      {isTutorRecipient ? (
        <Typography variant="body2">
          Pet(s): {petNames.length > 0 ? petNames.join(", ") : "—"}
        </Typography>
      ) : (
        <Typography variant="body2">
          Nascimento: {formatPatientBirthDateDisplay(patient.birthDate)}
        </Typography>
      )}
    </Stack>
  );
}

/** Detalhe de envio(s) do mesmo modelo para um destinatário (foco em modelos). */
export const CommunicationSendDetailDialog = memo(
  ({
    open,
    onClose,
    messages,
    patient,
    vetApp: vetAppProp,
    petNamesByTutorLogicalId,
  }: {
    open: boolean;
    onClose: () => void;
    messages: FluxZapCommunicationRecord[];
    patient: CrmPatientSummary | undefined;
    vetApp?: boolean;
    petNamesByTutorLogicalId?: Map<string, string[]>;
  }) => {
    const vetApp = vetAppProp ?? isVetApp();
    const message = messages[0] ?? null;
    const multipleSends = messages.length > 1;

    return (
      <Dialog
        open={open && messages.length > 0}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {multipleSends ? "Detalhes dos envios" : "Detalhes do envio"}
        </DialogTitle>
        <DialogContent dividers>
          {message ? (
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">
                  Modelo
                </Typography>
                <Typography variant="body1">{message.templateLabel}</Typography>
              </Stack>
              {multipleSends ? null : (
                <Stack spacing={0.5}>
                  <Typography variant="overline" color="text.secondary">
                    Enviado em
                  </Typography>
                  <Typography variant="body2">
                    {formatCommunicationsDateTimeDisplay(message.created)}
                  </Typography>
                </Stack>
              )}
              <Divider />
              <CommunicationsRecipientDetailSection
                patient={patient}
                vetApp={vetApp}
                petNamesByTutorLogicalId={petNamesByTutorLogicalId}
                fallbackReference={message.subjectPatientReference}
              />
              {multipleSends ? (
                <>
                  <Divider />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Envios realizados ({messages.length})
                  </Typography>
                  <Table
                    size="small"
                    aria-label="Datas dos envios deste modelo para o destinatário"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          Enviado em
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {messages.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell
                            align="center"
                            sx={communicationsRankTableBodyCellSx}
                          >
                            {formatCommunicationsDateTimeDisplay(m.created)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CommunicationSendDetailDialog.displayName = "CommunicationSendDetailDialog";

/** Histórico de envios do destinatário (foco em pacientes/tutores). */
export const PatientCommunicationsDetailDialog = memo(
  ({
    open,
    onClose,
    patient,
    messages,
    vetApp: vetAppProp,
    petNamesByTutorLogicalId,
  }: {
    open: boolean;
    onClose: () => void;
    patient: CrmPatientSummary | null;
    messages: FluxZapCommunicationRecord[];
    vetApp?: boolean;
    petNamesByTutorLogicalId?: Map<string, string[]>;
  }) => {
    const vetApp = vetAppProp ?? isVetApp();

    const dialogTitle = useMemo(() => {
      if (!patient) {
        return vetApp ? "Detalhes do destinatário" : "Detalhes do paciente";
      }
      return formatPatientListLabel(patient);
    }, [patient, vetApp]);

    return (
      <Dialog
        open={open && Boolean(patient)}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {patient ? (
            <Stack spacing={2}>
              <CommunicationsRecipientDetailSection
                patient={patient}
                vetApp={vetApp}
                petNamesByTutorLogicalId={petNamesByTutorLogicalId}
              />
              {patient.lastAppointmentAt && !vetApp ? (
                <Typography variant="body2">
                  Última consulta (período buscado):{" "}
                  {formatLastAppointmentDisplay(patient.lastAppointmentAt)}
                </Typography>
              ) : null}
              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>
                Mensagens enviadas ({messages.length})
              </Typography>
              <Table
                size="small"
                aria-label="Lista de envios do destinatário"
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      Modelo
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      Data
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell
                        align="center"
                        sx={communicationsRankTableBodyCellSx}
                      >
                        {m.templateLabel}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={communicationsRankTableBodyCellSx}
                      >
                        {formatCommunicationsDateTimeDisplay(m.created)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  },
);

PatientCommunicationsDetailDialog.displayName =
  "PatientCommunicationsDetailDialog";
